import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'clsx';
import dayjs from 'dayjs';
import { alpha, type Palette, type PaletteColor, useTheme } from '@mui/material/styles';

import { useChannelConfig } from '@/modules/ChannelList/constants/carousels';
import { ALPHA_800 } from '@/mui/constants/opacity';

import { useSmallScreen } from '../../helpers/customHooks';
import { isVisibleInConfig } from '@/modules/common/helpers/configuration';
import { getFormattedDuration } from '@/modules/common/helpers/dateTime';
import * as commonSelectors from '@/modules/common/redux/selectors/common';

import { Stack, Typography } from '@/mui/components';

import styles from './VideoInfo.module.scss';

type Props = {
  title?: string;
  description?: string;
  date: number;
  duration: number;
  isHovered: boolean;
  rows?: number;
  staticType?: boolean;
};

function VideoInfo({ title = '', description = '', rows = 3, date, duration, isHovered, staticType = false }: Props) {
  const theme = useTheme();
  const isSmallScreen = useSmallScreen();
  const typographyVariant = !isSmallScreen ? 'body2' : 'caption';
  const configuration = useSelector(commonSelectors.configurationSelector);
  const configByChannel = useChannelConfig();

  const dateFormat = configuration.common.dateTimeFormat;

  const overlayConfig = configByChannel.videoCard.overlay;
  const videoCardTitleConfig = configByChannel.videoCard.title;
  const videoCardDescriptionConfig = configByChannel.videoCard.description;
  const videoCardDateTimeConfig = configByChannel.videoCard.dateTime;
  const videoCardDurationConfig = configByChannel.videoCard.duration;

  const overlayValueColor = overlayConfig.backgroundColor
    ? (overlayConfig.backgroundColor.split('.').reduce((acc: Palette | PaletteColor | string, key: string) => {
        const colorKey = key as keyof typeof acc;
        const colorGroup = acc[colorKey];

        if (!colorGroup) {
          return '';
        }

        return colorGroup as typeof colorGroup;
      }, theme.palette) as string)
    : undefined;

  return (
    <Stack
      style={{
        backgroundColor: overlayValueColor
          ? alpha(overlayValueColor, overlayConfig.backgroundOpacity ?? ALPHA_800)
          : undefined,
      }}
      className={classNames(styles.Info, {
        [styles.Info___static]: staticType,
        [styles.Info___hovered]: isVisibleInConfig(overlayConfig.visible) && isHovered,
        [styles.Info___default]: !overlayValueColor,
      })}
      spacing={3}
      justifyContent={staticType ? 'flex-end' : undefined}
    >
      {staticType && title && isVisibleInConfig(videoCardTitleConfig.visible) && (
        <Typography
          title={title}
          data-a="video-title"
          style={{
            fontFamily: videoCardTitleConfig.fontFamily,
          }}
          className={classNames(styles.SlideTitle, {
            [styles.SlideTitle___default]: !videoCardTitleConfig.typography,
          })}
          color={videoCardTitleConfig.color}
          variant={videoCardTitleConfig.typography ?? 'inherit'}
          fontWeight={videoCardTitleConfig.fontWeight ?? 400}
          rows={1}
        >
          {title}
        </Typography>
      )}
      {description && !(isSmallScreen && staticType) && isVisibleInConfig(videoCardDescriptionConfig.visible) && (
        <Typography
          style={{
            fontFamily: videoCardDescriptionConfig.fontFamily,
          }}
          title={description}
          rows={rows}
          data-a="video-description"
          color={videoCardDescriptionConfig.color}
          fontWeight={videoCardDescriptionConfig.fontWeight}
          variant={videoCardDescriptionConfig.typography ?? typographyVariant}
        >
          {description}
        </Typography>
      )}
      <Stack spacing={0} justifyContent="space-between" direction="row" alignItems="center">
        {isVisibleInConfig(videoCardDateTimeConfig.visible) ? (
          <Typography
            style={{
              fontFamily: videoCardDateTimeConfig.fontFamily,
            }}
            variant={videoCardDateTimeConfig.typography ?? typographyVariant}
            component="div"
            fontWeight={videoCardDateTimeConfig.fontWeight}
            color={videoCardDateTimeConfig.color}
            data-a="video-date-time"
            whiteSpace="nowrap"
          >
            {dayjs(date).format(dateFormat)}
          </Typography>
        ) : (
          <div />
        )}
        {isVisibleInConfig(videoCardDurationConfig.visible) && (
          <Typography
            style={{
              fontFamily: videoCardDurationConfig.fontFamily,
              backgroundColor: videoCardDurationConfig.backgroundColor,
            }}
            data-a="video-duration"
            variant={videoCardDurationConfig.typography ?? typographyVariant}
            fontWeight={videoCardDurationConfig.fontWeight}
            className={classNames(styles.InfoMetaTime, {
              [styles.InfoMetaTime___default]:
                !videoCardDurationConfig.backgroundColor && !videoCardDurationConfig.color,
            })}
            component="div"
            color={videoCardDurationConfig.color}
          >
            {getFormattedDuration(duration)}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

export default VideoInfo;
